<template>
  <div class="body-product">
    <h1>{{ productHeading || this.productLocaleData.name }}</h1>
    
    <!-- desktop version -->
    <div class="body-product-div body-product-div-desktop">
      <div class="body-product-div__photo">
        <product-photo
          :product="product"
          :db-config="dbConfig"
          :product-properties="productProperties"
        />
      </div>

      <div class="product-content">
        <div v-if="$auth.loggedIn" class="flex-row align-center mb-2 gap-10" >
          <a 
            href="#"
            @click.prevent="switchFavourite" 
            :title="isFavourite ? $t('remove_favourites') : $t('add_favourites')"
            class="no-border"
          >
            <i class="svg-icon star" :class="{'star-favourite': isFavourite}" />
          </a>
          <template v-if="isFavourite">{{ $t('in_favourites') }}</template>
          <template v-else>{{ $t('add_favourites') }}</template>
        </div>

        <div class="checkbox mb-2" v-if="showExpressCheckbox">
          <label 
            for="checkbox-express"
            @click="notifyExpressCart"
            :class="{'checked': express}"
          >
            <input 
              v-model="express"
              :disabled="disabledExpressCheckbox"
              class="styled-checkbox"
              id="checkbox-express"
              type="checkbox"
            >
            {{ $t('express') }}</label>
        </div>

        <div class="price" v-if="!$noCartMode()">
          <div class="price-header hide-phone" :class="{'no-price': !product.price > 0}">
            <div class="store-cell" v-if="product.price > 0"><span>{{ $t('stock') }}</span></div>
            <div class="availability-cell" v-if="product.price > 0"><span>{{ $t('availability') }}</span></div>
            <div class="price-cell">
              <span>{{ $t('price_rub') }}</span>
              <span class="upper">{{ $t('no_vat') }}</span>
            </div>
            <div class="order-cell" :class="{'quantity': product.price <= 0}">
              <span v-if="product.price > 0">{{$t('order')}}</span><span v-else>{{$t('quantity') }}</span>
            </div>
          </div>

          <div class="price-values">
            <div class="price-stores-lines" :class="{'no-price': !product.price > 0}">
              <template v-if="product.price > 0">
                <div v-for="stockType in ['available', 'transit', 'preorder']" v-if="product.amount_free[stockType] > 0 || stockType === 'preorder'" class="price-stores">
                  <div class="store-col" :class="{'express': checkExpress(stockType)}">
                    <span v-if="stockType === 'available'" class="name-store">{{ $t('available') }}</span>
                    <span 
                      v-else-if="stockType === 'transit'" 
                      class="name-store"
                      :class="{'transit': !checkExpress(stockType)}"
                      @mouseover="showTransit = !showTransit && !checkExpress(stockType)"
                      @mouseleave="showTransit = false">{{ $t('transit') }}</span>
                    <span v-else-if="stockType === 'preorder'" class="name-store"
                          :class="{'transit': !checkExpress(stockType)}"
                          @mouseover="showPreorder = !showPreorder && !checkExpress(stockType)"
                          @mouseleave="showPreorder = false">{{ $t('remote') }}</span>
                  </div>
                  <div class="availability-col" :class="{'express': checkExpress(stockType)}">
                    <span v-if="stockType !== 'preorder'">{{ product.amount_free[stockType] | integerPart }} {{ $t('pcs') }}</span>
                    <span v-else>{{ $t('preorder') }}</span>
                  </div>
                  <div class="price-col" :class="{'express': checkExpress(stockType)}">
                    <span class="price-digits">
                      {{ product.price | integerPart }}
                      <span class="small">{{ product.price | fractionalPart }}</span> {{ $t('rub') }}
                    </span>
                  </div>

                  <Transition name="fade">
                    <div v-show="showTransit && stockType === 'transit'" class="transit-tooltip on-transit">
                      <span>{{ product.amount_free.transit | integerPart }} {{ $t('pcs_transit') }}</span>
                      <template v-if="product.shipment_date">
                        <span>{{ $t('expected_shipment_date') }}: {{ product.shipment_date }}</span>
                      </template>
                      <span v-else>
                        {{ deliveryDeadline('transit') }}
                        {{ shipmentDays('transit') }}
                        {{ shipmentDays('transit') | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}
                      </span>
                    </div>
                  </Transition>

                  <Transition name="fade">
                    <div v-show="showPreorder && stockType === 'preorder'" class="transit-tooltip on-order">
                      <span>
                        {{ deliveryDeadline('preorder') }}
                        {{ shipmentDays('preorder') }}
                        {{ shipmentDays('preorder') | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}</span>
                    </div>
                  </Transition>
                </div>
              </template>
              <template v-else>
                <div class="price-stores">
                  <div class="price-col">
                    <span>{{ $t('on_request') }}</span>
                  </div>
                </div>
              </template>
            </div>

            <div class="order">
              <div class="count">
                <a href="#" @click.prevent="decreaseAmount" :aria-label="$t('decrease')">
                  <i class="svg-icon minus icon-pointer"></i></a>
                <input-number v-model="amountCart" :value="amountCart" :min-value="0"/>
                <a href="#" @click.prevent="increaseAmount" :aria-label="$t('increase')">
                  <i class="svg-icon plus icon-pointer"></i></a>
              </div>
              <div class="cart-price">
                <button 
                  v-if="product.price > 0"
                  href="#"
                  class="button"
                  @click.prevent="addCart"
                  :aria-label="$t('add_to_cart')"
                  :class="{'product-cart-exists': productAmountCart > 0}"
                ><i class="svg-icon cart-icon" /><span v-if="productAmountCart > 0">{{ productAmountCart | integerPart }}</span>
                </button>
                <button 
                  v-else
                  href="#"
                  class="button"
                  @click.prevent="addPriceRequest"
                  :aria-label="$t('request_price')"
                  :class="{'product-cart-exists': product.amount_price_request > 0}"
                ><i class="svg-icon preorder" /><span v-if="product.amount_price_request > 0">{{ product.amount_price_request | integerPart }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Показываем склады поставщиков, если есть -->
          <template v-if="canShowSuppliers(product)">
            <template v-for="supplier in product.suppliers">
              <ProductDetailsSupplier
                v-if="!hasOwn(product) || supplier.stock.is_force_visible"
                :key="`supplier-${supplier.id}`"
                :product="product"
                :supplier="supplier"
              />
            </template>
          </template>
        </div>

        <div class="articles">
          <div class="articles-table">
            <div>
              <span class="name">{{ $t('product_code') }}</span>
              <span>{{ product.code }}</span>
            </div>
            <div v-for="prop in displayedProperties.filter(x => x.visibility === 'top')">
              <span class="name">{{ getPropertyLocaleName(prop) }}</span>
              <span v-if="getPropertyValueLocaleData(prop).multiple_values">
                {{ getPropertyValueLocaleData(prop).multiple_values.join(', ') }}
              </span>
              <span v-else>{{ getPropertyValueLocaleData(prop).value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div 
      v-if="relatedProducts && relatedProducts.length"
      class="related-products-list products-list"
      :class="{'products-list-limited': hideStockAvailabilityColumns}"
    >
      <h2>{{ $t('related_products_header') }}</h2>
      <products-list-header />
      <products-list-item v-for="p in relatedProducts" :key="p.id" :product="p" :stocks="stocks" :express="express" />
    </div>

    <div class="description-dimensions" v-if="productLocaleData.description">
      <h2>{{ $t('product_description') }}</h2>
      <div class="description" v-html="productLocaleData.description" />
    </div>
    
    <product-car-model-details v-if="vendor === 'wellfar'" :product="product" />

    <modal :visible="showPriceRequestModal" @close="showPriceRequestModal = false" v-if="!$noCartMode()">
      <template v-slot:header>
        <div>{{ $t('price_request_saved_1') }}<br>{{ $t('price_request_saved_2') }}</div>
      </template>
      <template v-slot:body>
        <div></div>
      </template>
      <template v-slot:footer>
        <button class="modal-default-button" @click.prevent="$router.push({path: localePath('/price-request/')})">
          {{ $t('go_to_price_request_page') }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import {Stock} from '@/models/stock';
import {ProductFavourite} from '@/models/product';

import InputNumber from '@/components/controls/input-number';
import Modal from '@/components/modal';
import ProductCarModelDetails from '@/components/catalog/product-car-model-details';
import ProductsListHeader from '@/components/catalog/products-list-header';
import ProductsListItem from '@/components/catalog/products-list-item';
import ProductPhoto from '@/components/catalog/product-photo';
import ProductDetailsSupplier from '@/components/catalog/product-details-supplier';

export default {
  components: {
    InputNumber,
    Modal,
    ProductCarModelDetails,
    ProductsListHeader,
    ProductsListItem,
    ProductPhoto,
    ProductDetailsSupplier,
  },
  name: 'Product',
  props: ['product', 'productProperties', 'dbConfig', 'productHeading'],
  data() {
    return {
      amountCart: 0,
      express: false,
      stocks: [],
      relatedProducts: [],
      showTransit: false,
      showPreorder: false,
      showPriceRequestModal: false,
      isFavourite: false,
    }
  },
  computed: {
    vendor() {
      return process.env.VENDOR
    },
    hideStockAvailabilityColumns() {
      return this.vendor === 'wellfar' && !this.$auth.loggedIn;
    },
    productLocaleData() {
      return this.product?.product_translations?.find(x => x.language.slug === this.$i18n.locale) || {};
    },
    displayedProperties() {
      const productPropertiesIds = this.product.product_properties.filter(
          x => x.value && (!x.language || x.language.slug === this.$i18n.locale)
      ).map(x => x.property);
      return this.productProperties.filter(x => productPropertiesIds.includes(x.id));
    },
    disabledExpressCheckbox() {
      return this.$store.getters.cartExists.count > 0;
    },
    showExpressCheckbox() {
      return false;
      return this.$auth.loggedIn && this.$auth.hasScope('is_extra') && (
          !this.$store.getters.cartExists.count || this.$store.getters.cartExists.express) && this.product.price > 0;
    },
    productAmountCart() {
      return Object.values(this.product.amount_cart).reduce((a, b) => a + b, 0);
    },
  },
  async fetch() {
    [this.stocks, this.relatedProducts] = await Promise.all([
      Stock.config({count_loading: true}).get(),
      this.$axios.$get(`/catalog/product/${this.product.slug}/related-products/`),
    ]);
    this.isFavourite = this.product.is_favourite;
  },
  created() {
    this.$store.dispatch('setCartExists', () => {
      if (this.$store.getters.cartExists.count) {
        this.express = this.$store.getters.cartExists.express;
        if (this.express) {
          this.notifyExpressCart();
        }
      }
    });
    this.isFavourite = this.product.is_favourite;
  },
  methods: {
    shipmentDays(stockType) {
      return this.stocks.find(x => x.stock_type === stockType)?.shipment_days;
    },
    deliveryDeadline(stockType){
      const dl = this.stocks.find(x => x.stock_type === stockType)?.shipment_days_limit;
      if (dl === 'gt'){
        return this.$t('delivery_deadline_from') 
      }
      else if (dl === 'lt'){
        return this.$t('delivery_deadline_to') 
      }
      return this.$t('delivery_deadline') 
    },
    switchFavourite() {
      if (this.$auth.loggedIn) {
        let fav = new ProductFavourite({product: this.product.id});
        if (this.isFavourite) {
          fav.id = this.product.id;
          fav.delete().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = false;
            this.$notify({
              duration: 1000,
              text: this.$t('product_removed_from_fav'),
            });
            this.$emit('remove-favourite');
          });
        } else {
          fav.save().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = true;
            this.$notify({
              duration: 1000,
              text: this.$t('product_added_to_fav'),
            });
          });
        }
      }
    },
    hasOwn(product){
      return product.amount_free?.available || product.amount_free?.transit;
    },
    canShowSuppliers(product){
      return product.suppliers.length > 0 && (!this.hasOwn(product) || product.suppliers.some(x => x.stock.is_force_visible));
      // return product.suppliers.length > 0 && !(product.amount_free?.available || product.amount_free?.transit);
    },
    getPropertyLocaleName(prop) {
      let data = prop?.property_translations?.find(x => x.language.slug === this.$i18n.locale) || {};
      return data.name || prop.name;
    },
    getPropertyValueLocaleData(prop) {
      return this.product.product_properties?.find(
          x => x.property === prop.id && x.value && (!x.language || x.language.slug === this.$i18n.locale)
      ) || {};
    },
    decreaseAmount() {
      if (this.amountCart > 0) {
        let amount = this.amountCart - this.product.package_quantity;
        if (amount % this.product.package_quantity) {
          amount += this.product.package_quantity - amount % this.product.package_quantity;
        }
        this.amountCart = amount;
      }
    },
    increaseAmount() {
      let amount = this.amountCart + this.product.package_quantity;
      amount -= amount % this.product.package_quantity;
      this.amountCart = amount;
    },
    addCart() {
      const data = {
        product: this.product.id,
        amount: this.amountCart,
        express: this.express,
      };
      this.$axios.$post('/order/update-cart/', data)
          .then((response) => {
            this.$store.dispatch('setCartExists');
            this.product.amount_free = response.amount_free_dict;
            this.product.amount_cart = response.amount_cart_dict;
            this.amountCart = 0;
            this.$notify({
              duration: 1000,
              text: this.$t('product_added_to_cart'),
            });
          })
          .catch((e) => {
            let msg = this.$t('unknown_error');
            try {
              msg = e.response.data.detail;
            } catch (e) {
            } finally {
              this.$notify({
                duration: 5000,
                text: msg,
              });
            }
          });
    },
    addPriceRequest() {
      const data = {
        product: this.product.id,
        amount: this.amountCart,
      };
      this.$axios.$post('/order/price-request/add-price-request/', data)
        .then((response) => {
          this.$store.dispatch('setPriceRequestExists');
          this.showPriceRequestModal = true;
          this.product.amount_price_request = response.amount_price_request;
          this.amountCart = 0;
        })
        .catch((e) => {
          let msg = this.$t('unknown_error');
          try {
            msg = e.response.data.detail;
          } catch (e) {
          } finally {
            this.$notify({
              duration: 5000,
              text: msg,
            });
          }
        });
    },
    checkExpress(stockType) {
      return this.express && ['transit', 'preorder'].includes(stockType);
    },
    notifyExpressCart() {
      if (this.disabledExpressCheckbox) {
        this.$notify({
          duration: 5000,
          text: this.$t('express_cart_warning'),
        });
      }
    },
  },
}
</script>

<i18n lang="yaml">
ru:
  available: 'В наличии'
  transit: 'В пути'
  preorder: 'под заказ'
  remote: 'Удаленный'
  increase: 'Увеличить количество'
  decrease: 'Уменьшить количество'
  add_to_cart: 'Добавить в корзину'
  to_cart: 'В корзину'
  stock_pcs: 'Склад, шт.'
  product_added_to_cart: 'Товар добавлен в корзину.'
  express_cart_warning: 'Т.к. создана экспресс-корзина, товар можно добавить только в экспресс-заказ.'
  related_products_header: 'Связанные товары'
  product_description: 'Описание товара'
  stock: 'Склад'
  order: 'Заказать'
  availability: 'Наличие'
  quantity: 'Количество'
  add_any_quantity: 'добавьте в корзину любое количество'
  no: 'Нет'
  request_price: 'Запросить цену'
  price_request_saved_1: 'Ваш запрос сохранен в папке'
  price_request_saved_2: '«Мои запросы»'
  go_to_price_request_page: 'Перейти в «Мои запросы»'
en:
  available: 'available'
  transit: 'in transit'
  preorder: 'preorder'
  remote: 'Remote'
  increase: 'Increase quantity'
  decrease: 'Decrease quantity'
  add_to_cart: 'Add to cart'
  to_cart: 'Add to cart'
  stock_pcs: 'Stock, pcs.'
  product_added_to_cart: 'Product is added to cart.'
  express_cart_warning: 'Only express order is available because express cart exists.'
  related_products_header: 'Related products'
  product_description: 'Product description'
  stock: 'Stock'
  order: 'Order'
  availability: 'Availability'
  quantity: 'Quantity'
  add_any_quantity: 'enter any quantity to cart'
  no: 'No'
  request_price: 'Request a price'
  price_request_saved_1: 'Your request has been saved on'
  price_request_saved_2: '«My requests» page'
  go_to_price_request_page: 'Go to «My requests»'
</i18n>
